// `core` + the metadata passed explicitly (what the default entry does
// internally): the app's CommonJS test run otherwise loads the metadata JSON
// as `{ default }` and every number throws.
import {
  parsePhoneNumberFromString,
  type CountryCode,
  type PhoneNumber,
} from "libphonenumber-js/core";
import metadata from "libphonenumber-js/min/metadata";

/**
 * libphonenumber can mark very short DE partials (e.g. +491512) as valid — gate by
 * min national length. German landlines vary in length (often 6–9 digits); mobiles
 * starting with 1 need at least 10 national digits.
 */
const MIN_NATIONAL_DIGITS: Partial<Record<CountryCode, number>> = {
  AT: 10,
  CH: 9,
};

const DE_MOBILE_MIN_NATIONAL_DIGITS = 10;
const DE_LANDLINE_MIN_NATIONAL_DIGITS = 6;

function passesGermanNationalLengthGate(nationalNumber: string): boolean {
  if (nationalNumber.startsWith("1")) {
    return nationalNumber.length >= DE_MOBILE_MIN_NATIONAL_DIGITS;
  }
  return nationalNumber.length >= DE_LANDLINE_MIN_NATIONAL_DIGITS;
}

function passesNationalLengthGate(parsed: PhoneNumber): boolean {
  const country = parsed.country;
  if (!country) return true;
  if (country === "DE") {
    return passesGermanNationalLengthGate(parsed.nationalNumber);
  }
  const min = MIN_NATIONAL_DIGITS[country];
  if (min === undefined) return true;
  return parsed.nationalNumber.length >= min;
}

/** Validate phone to E.164 or return null if invalid. */
export function validatePhoneToE164(
  raw: string,
  defaultCountry: CountryCode = "DE",
): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const parsed = parsePhoneNumberFromString(trimmed, defaultCountry, metadata);
  if (!parsed?.isValid() || !passesNationalLengthGate(parsed)) return null;
  return parsed.format("E.164");
}

/** True when raw parses to a valid phone number for the given default country. */
export function isValidBookingPhone(
  raw: string,
  defaultCountry: CountryCode = "DE",
): boolean {
  return validatePhoneToE164(raw, defaultCountry) !== null;
}
